<template>
  <Layout
    class="onboarding-genre-selection"
    :title="page.title"
    :subtitle="page.subtitle"
    :scrollDefaultSlot="true"
  >
    <svg
      class="onboarding-genre-selection__svg"
      viewBox="0 0 402 380"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
    >
      <g>
        <path
          d="M145.304 35.083c-.115.424.472.662.684.277l1.779-3.233a10.312 10.312 0 0 1 11.753-4.975l3.56.974c.424.116.662-.472.277-.684l-3.233-1.78a10.31 10.31 0 0 1-4.975-11.752l.973-3.56c.116-.424-.471-.662-.683-.277l-1.78 3.233a10.31 10.31 0 0 1-11.752 4.975l-3.56-.973c-.424-.116-.662.471-.277.683l3.233 1.78a10.312 10.312 0 0 1 4.975 11.752l-.974 3.56Z"
        />
        <path
          d="M191.262 258.857c.184.298.642.085.532-.247l-.916-2.791a8.208 8.208 0 0 1 3.484-9.542l2.499-1.544c.298-.184.085-.641-.248-.532l-2.79.916a8.206 8.206 0 0 1-9.542-3.484l-1.544-2.499c-.184-.298-.641-.085-.532.248l.916 2.791a8.205 8.205 0 0 1-3.484 9.541l-2.499 1.544c-.298.184-.085.642.248.532l2.79-.916a8.208 8.208 0 0 1 9.542 3.484l1.544 2.499Z"
        />
        <path
          d="m30.11 106.999 18.09 22.9h-8.345l3.234 17.9-18.09-22.9h8.345l-3.234-17.9Z"
        />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M6.868 310.999 0 306.96l5.062-8.922 6.872 4.033.004-8.072h10.124l.004 8.072 6.872-4.033L34 306.96l-6.868 4.039L34 315.038l-5.062 8.922-6.872-4.033-.004 8.072H11.938l-.004-8.072-6.872 4.033L0 315.038l6.868-4.039Z"
        />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M360.444 24.999 356 22.385l3.275-5.772 4.447 2.609.003-5.223h6.55l.003 5.223 4.447-2.61L378 22.386 373.556 25 378 27.613l-3.275 5.772-4.447-2.609-.003 5.223h-6.55l-.003-5.223-4.447 2.61L356 27.612l4.444-2.614Z"
        />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M391.877 273.167c8.1-8.786 16.005 4.21 3.903 6.315 12.102 2.196 4.099 15.1-3.903 6.315 4.099 10.982-11.81 10.89-7.71 0-8.101 8.785-16.006-4.119-3.904-6.315-12.2-2.105-4.197-15.009 3.904-6.315-4.197-10.891 11.809-10.891 7.71 0Zm-3.904 4.668c.976 0 1.854.732 1.854 1.647s-.878 1.739-1.854 1.739c-.976 0-1.757-.824-1.757-1.739 0-.915.781-1.647 1.757-1.647Z"
        />
      </g>

      <GenreSelectionOption
        v-for="(option, i) in page.options"
        :key="option.id"
        class="onboarding-genre-selection__option"
        :option="option"
        :isSelected="isSelected(option.id)"
        :index="i"
        @click="toggleOption(option.id)"
      />
    </svg>
  </Layout>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'

import type { QuestionnaireView } from '@/models/onboarding'
import type { GenreSelectionPage } from '@/models/onboarding.types'

import Layout from '../Layout.vue'
import GenreSelectionOption from '../GenreSelectionOption.vue'

const props = defineProps<{
  view: QuestionnaireView
}>()

const page = computed(() => {
  return props.view.currentPage as GenreSelectionPage
})

const selectedOptions = ref<string[]>([])

watch(selectedOptions, (newValue) => {
  props.view.setCurrentChoice(newValue)
})

function toggleOption(option: string): void {
  const newValue = selectedOptions.value.includes(option)
    ? selectedOptions.value.filter((value) => value !== option)
    : [...selectedOptions.value, option]

  selectedOptions.value = newValue
}

function isSelected(option: string): boolean {
  return selectedOptions.value.includes(option)
}

onMounted(() => {
  // The default value of `view.currentChoice` is `undefined`.
  // The value of the choices for this page is an array, so
  // we set the initial value of `view.currentChoice` to an
  // empty array so that we can spread the current value
  // without having to check if it's an array first.
  props.view.setCurrentChoice([])
})
</script>

<style lang="scss" scoped>
.onboarding-genre-selection {
  overflow-x: hidden;

  :deep(.onboarding-layout__default-slot) {
    display: flex;
    justify-content: center;
    align-items: center;
    width: calc(100% + var(--onboarding-padding) * 2);
    overflow-x: hidden;

    @media (max-height: 540px) {
      align-items: flex-start;
    }
  }

  &__svg {
    width: 400px;
    max-width: none;
    max-height: 100%;
    min-height: 260px;
    flex-shrink: 0;
    fill: $c-border-7;
    overflow: visible;
  }
}
</style>
